import { type PlaceLabel, PlaceLabels } from './place-labels';

export type RoadDetail = 'none' | 'highways' | 'main' | 'all';
export type AreaKind = 'world' | 'country' | 'state';

/** What a whole country, state or world map shows (the map-poster Lambda's area options) */
export interface AreaOptionsValue {
  roadDetail: RoadDetail;
  showBoundaries: boolean;
  colourStates: boolean;
  grid: boolean;
  /** The customer's own places, each marked with a heart */
  labels: PlaceLabel[];
  /** The map's automatic city names */
  showCities: boolean;
}

export const DEFAULT_AREA_OPTIONS: AreaOptionsValue = {
  roadDetail: 'main',
  showBoundaries: true,
  colourStates: false,
  grid: false,
  labels: [],
  showCities: true,
};

const ROAD_DETAIL_OPTIONS: { value: RoadDetail; label: string; description: string }[] = [
  { value: 'none', label: 'None', description: 'Outline, rivers and borders only' },
  { value: 'highways', label: 'Highways', description: 'Major highways' },
  { value: 'main', label: 'Main', description: 'Highways and main roads' },
  { value: 'all', label: 'All', description: 'Every major road' },
];

/** The boundaries a map draws between its divisions: states within a country, countries across the world */
const DIVISIONS: Record<Exclude<AreaKind, 'state'>, { one: string; many: string }> = {
  country: { one: 'state', many: 'States' },
  world: { one: 'country', many: 'Countries' },
};

interface AreaOptionsProps {
  value: AreaOptionsValue;
  onChange: (value: AreaOptionsValue) => void;
  /** A country has states and the world has countries to draw and colour; a state map hides those options */
  kind: AreaKind;
  disabled?: boolean;
}

function Toggle({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className="flex cursor-pointer items-start justify-between gap-4">
      <span>
        <span className="text-primary-2-80 block text-sm font-medium">{label}</span>
        <span className="text-neutral-2-60 block text-xs">{description}</span>
      </span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="text-secondary-1 focus:ring-secondary-1 border-neutral-2-20 mt-1 h-4 w-4 rounded disabled:cursor-not-allowed"
      />
    </label>
  );
}

export function AreaOptions({ value, onChange, kind, disabled }: AreaOptionsProps) {
  const current = ROAD_DETAIL_OPTIONS.find((option) => option.value === value.roadDetail)!;
  const divisions = kind === 'state' ? null : DIVISIONS[kind];

  return (
    <div className="space-y-5">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-primary-2-80 block text-sm font-medium">Road Detail</label>
          <span className="text-neutral-2-40 text-xs">{current.description}</span>
        </div>
        <div className="grid grid-cols-4 gap-1">
          {ROAD_DETAIL_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange({ ...value, roadDetail: option.value })}
              disabled={disabled}
              className={`rounded-md px-2 py-2 text-xs font-medium transition-colors ${
                value.roadDetail === option.value
                  ? 'bg-secondary-1 text-white'
                  : 'bg-neutral-2-10 text-neutral-2-80 hover:bg-neutral-2-20'
              } disabled:cursor-not-allowed disabled:opacity-50`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {divisions && (
        <>
          <Toggle
            label={kind === 'world' ? 'Country Borders' : 'State Boundaries'}
            description={`Lines between the ${divisions.many.toLowerCase()}`}
            checked={value.showBoundaries}
            onChange={(showBoundaries) => onChange({ ...value, showBoundaries })}
            disabled={disabled}
          />
          <Toggle
            label={`Colour ${divisions.many}`}
            description={`A different shade for each ${divisions.one}, easier to sort pieces`}
            checked={value.colourStates}
            onChange={(colourStates) => onChange({ ...value, colourStates })}
            disabled={disabled}
          />
        </>
      )}
      <Toggle
        label="Show Major Cities"
        description="The names of the biggest cities"
        checked={value.showCities}
        onChange={(showCities) => onChange({ ...value, showCities })}
        disabled={disabled}
      />
      <Toggle
        label="Grid & Frame"
        description="Latitude and longitude lines with a border"
        checked={value.grid}
        onChange={(grid) => onChange({ ...value, grid })}
        disabled={disabled}
      />

      <PlaceLabels value={value.labels} onChange={(labels) => onChange({ ...value, labels })} disabled={disabled} />
    </div>
  );
}
